// ---------------------------------------------------------------------------
// draftTransformers.js
//
// Mobile-app drafts store their `form_data` in the raw camelCase state shape
// used by the React-Native create forms. The web view modals + edit forms,
// however, expect the `json_data` shape produced when a form is submitted to
// the API (an array of { question, answer } objects — except AIF, which is a
// single-object array).
//
// These helpers convert a draft's mobile `form_data` into the corresponding
// web `json_data` so the same view modals (DraftsDashboard) and the same
// mapJsonToForm() loaders (edit forms) can consume drafts without changes.
//
// Form-type codes: AIF, BBS, WDS/WDLS, SHP/SHPA, TRAILER.
// ---------------------------------------------------------------------------

import { isUploadedRef, toDisplayUrl } from './draftImageUtils';

// Normalize a single image value to a display-ready shape.
// Uploaded refs → { file_name, file_path, displayUrl }
// base64/URL strings → returned as-is.
const normImg = (img) => {
  if (isUploadedRef(img)) {
    return { ...img, displayUrl: toDisplayUrl(img) };
  }
  return img;
};

// Normalize an array of images (or a single image) for display.
const normImgArray = (arr) => {
  if (!arr) return arr;
  if (Array.isArray(arr)) return arr.map(normImg);
  return normImg(arr);
};

// ============================ WDS (Weekly Driver Log) =======================

const WDS_CHECKS1_MAP = {
  accidentForm: 'ACCIDENT FORM',
  battery: 'BATTERY',
  belts: 'BELTS',
  brakeFluid: 'BRAKE FLUID',
  engineOil: 'ENGINE OIL',
  fireExtinguisher: 'FIRE EXTINGUISHER (10lb)',
  firstAidKit: 'FIRST AID KIT',
  headlamps: 'HEADLAMPS & HI-BEAMS',
  heaterHoses: 'HEATER HOSES',
  hitchReceiver: 'HITCH, RECEIVER, AND PINS',
  horn: 'HORN',
  insuranceCard: 'INSURANCE CARD',
  jackTireIron: 'JACK & TIRE IRON',
};

const WDS_CHECKS2_MAP = {
  lights: 'LIGHTS (Parking & BRAKE)',
  mirrors: 'MIRRORS',
  parkingBrake: 'PARKING BRAKE',
  radiatorFluid: 'RADIATOR FLUID',
  radiatorHoses: 'RADIATOR HOSES',
  seatbelts: 'SEATBELTS',
  spareTire: 'SPARE TIRE',
  steeringFluid: 'STEERING FLUID',
  tirePsi: 'TIRE PSI',
  transmissionOil: 'TRANSMISSION OIL',
  turnSignals: 'TURN SIGNALS',
  washerFluid: 'WASHER FLUID',
  windshieldWipers: 'WINDSHIELD WIPERS',
};

export const buildWdsJsonData = (formData = {}) => {
  const json = [];

  if (formData.unitNumber) {
    json.push({ question: 'Driver Information - Unit Number', answer: formData.unitNumber });
  }

  json.push({
    question: 'Driver Information - Fuel Allocation',
    answer: formData.fuelAllocation || '',
  });

  if (Array.isArray(formData.logEntries) && formData.logEntries.length > 0) {
    json.push({
      question: 'Driver Log Entries',
      answer: formData.logEntries.map((entry) => {
        const fillUps = (entry.fillUps || []).map((fu) => {
          const fuAlloc = fu.fuelAllocation || 'vehicle';
          if (fuAlloc === 'equipment') {
            return {
              gallons: fu.gallons || '',
              amount: fu.totalAmount || fu.amount || '',
              equipmentDescription: fu.equipmentDescription || '',
              fuelAllocation: 'equipment',
            };
          }
          return {
            location: fu.location || '',
            gallons: fu.gallons || '',
            amount: fu.totalAmount || fu.amount || '',
            mileage: fu.mileageAtRepair || fu.mileage || '',
            fuelAllocation: 'vehicle',
          };
        });
        const first = fillUps[0] || {};
        return {
          date: entry.date || '',
          dayLabel: entry.dayLabel || '',
          mileage: first.mileage || '',
          Start: entry.mileageStart || '',
          End: entry.mileageEnd || '',
          location: first.location || '',
          gallons: first.gallons || '',
          amount: first.amount || '',
          Job: entry.jobNumber || '',
          fillup: !!entry.fillup,
          dayOff: !!entry.dayOff,
          fillUps,
        };
      }),
    });
  }

  if (formData.vehicleChecks1 && typeof formData.vehicleChecks1 === 'object') {
    Object.entries(formData.vehicleChecks1).forEach(([key, value]) => {
      const label = WDS_CHECKS1_MAP[key];
      if (label && value) {
        json.push({ question: ` Vehicle Safety Check (Part-1) - ${label}`, answer: value });
      }
    });
  }

  if (formData.vehicleChecks2 && typeof formData.vehicleChecks2 === 'object') {
    Object.entries(formData.vehicleChecks2).forEach(([key, value]) => {
      const label = WDS_CHECKS2_MAP[key];
      if (label && value) {
        json.push({ question: ` Vehicle Safety Check (Part-2) - ${label}`, answer: value });
      }
    });
  }

  if (formData.comments) {
    json.push({ question: 'Comments', answer: formData.comments });
  }
  if (formData.checkedBy) {
    json.push({ question: 'Checked By', answer: formData.checkedBy });
  }
  if (formData.signature) {
    const sigArr = Array.isArray(formData.signature) ? formData.signature : [formData.signature];
    json.push({ question: 'Signature', answer: normImgArray(sigArr) });
  }
  if (Array.isArray(formData.uploadedImages) && formData.uploadedImages.length > 0) {
    json.push({ question: 'Comments - Uploaded Images', answer: normImgArray(formData.uploadedImages) });
  }

  return json;
};

// ============================ BBS (Behavior Based Safety) ====================

const BBS_PPE = {
  eyeFace: 'Eye & Face',
  hand: 'Hand',
  body: 'Body',
  head: 'Head',
  respiratory: 'Respiratory',
  hearing: 'Hearing',
};
const BBS_BODY = {
  bodyPositioning: 'Proper Body Positioning (Line of Fire)',
  liftingMechanics: 'Manual lifting mechanics',
  repetition: 'Repetition',
  pinchPoints: 'Pinch Points',
  pullingPushing: 'Pulling/Pushing',
  extending: 'Extending',
};
const BBS_ENV = {
  slipsTrips: 'Slips, Trips, Falls',
  congestedArea: 'Congested Work Area',
  barricades: 'Barricades/Signage',
  excavations: 'Excavations',
  fallProtection: 'Fall Protection',
  spillControl: 'Spill Control',
};
const BBS_PROC = {
  jsaCover: 'JSA Cover Task',
  hazardRecognition: 'Hazard Recognition',
  energyIsolation: 'Energy Isolation (LOTO)',
  simultaneousOps: 'Simultaneous Operations (SIMOPS)',
  tools: 'Hand/Power Tools',
  mobileEquipment: 'Mobile Equipment',
};

const pushAssessmentSection = (json, prefix, map, source) => {
  const obj = source && typeof source === 'object' ? source : {};
  Object.entries(map).forEach(([key, label]) => {
    const val = obj[key];
    json.push({
      question: `${prefix} - ${label}`,
      answer: val === 'NA' || val === undefined ? '' : val,
    });
  });
};

export const buildBbsJsonData = (formData = {}) => {
  const json = [];

  json.push({
    question: 'What task(s) were employee(s) performing during the BBS observation?',
    answer: formData.taskDescription || '',
  });

  pushAssessmentSection(json, 'PPE', BBS_PPE, formData.assessments);
  pushAssessmentSection(json, 'Body Position', BBS_BODY, formData.assessments2);
  pushAssessmentSection(json, 'Work Environment', BBS_ENV, formData.assessments31);
  pushAssessmentSection(json, 'Work Procedure', BBS_PROC, formData.assessments32);

  json.push({
    question: "Observer's feedback (Action Taken or Recommendation)",
    answer: formData.feedback || '',
  });
  json.push({
    question: 'Stop Work Authority (SWA) USED?',
    answer: formData.stopWorkUsed ? 'YES' : 'NO',
  });
  json.push({
    question: 'Client Joint Observation',
    answer: normImg(formData.signature) || '',
  });

  return json;
};

// ============================ TRAILER (Trailer Inspection) ===================

export const buildTrailerJsonData = (formData = {}) => {
  const d = formData;
  return [
    { question: 'Name of Person Assigned To', answer: d.emp_name || '' },
    { question: 'Trailer Unit #', answer: d.trailerUnit || '' },
    { question: 'Last Known Job Number', answer: d.job_id || '' },
    { question: 'Trailer Type', answer: d.trailerType || '' },
    { question: 'Is License Registration Current?', answer: d.licenseRegistration || '' },
    { question: 'Insurance Card / Current Cards?', answer: d.insuranceCard || '' },
    { question: 'Does ball size match to trailer hitch size?', answer: d.ballSizeMatch || '' },
    {
      question: 'Does your trailer need Fire Extinguisher? (No for utility trailers)',
      answer: d.fireExtinguisher || '',
    },
    { question: 'Tire Condition', answer: d.tireCondition || '' },
    { question: 'Axel Condition', answer: d.axelCondition || '' },
    { question: 'Brake Condition', answer: d.brakeCondition || '' },
    { question: 'Jack and Safety Chain Condition', answer: d.jackSafetyChain || '' },
    { question: 'Turn Signal Lights', answer: d.turnSignalLights || '' },
    { question: 'Brake Lights', answer: d.brakeLights || '' },
    { question: 'Proper Tie Downs', answer: d.properTieDowns || '' },
    { question: 'Trailer Hitch and Door Locks', answer: d.trailerHitchDoorLocks || '' },
    { question: 'Comments - Any Physical Damage', answer: d.comments || '' },
  ];
};

// ============================ SHP (Safety Health Audit) ======================

// Section name (as used in the submitted json_data question prefix) → state
// object key on the mobile draft form_data, plus the shared label map.
const SHP_SECTIONS = [
  ['PPE', 'ppeChecks'],
  ['Pre-Job Planning', 'preJobChecks'],
  ['Site Safety', 'sitePostingsChecks'],
  ['Work Environment', 'generalSiteChecks'],
  ['Working at Heights', 'heightsChecks'],
  ['Tools Electrical', 'toolsChecks'],
  ['Welding Gas', 'weldingChecks'],
  ['Excavation', 'excavationChecks'],
  ['Subcontractor', 'subcontractorChecks'],
  ['Emergency Plan', 'emergencyPlanChecks'],
];

const SHP_LABEL_MAP = {
  // PPE
  hardHats: 'Hard hats worn properly',
  safetyGlasses: 'Safety glasses with side shields',
  grindingProtection: 'Grinding protection (V2G goggles + face shield)',
  safetyFootwear: 'Safety-toed footwear',
  frcClothing: 'FRC clothing (long sleeves/pants or coveralls)',
  hearingProtection: 'Hearing protection in designated areas',
  properGloves: 'Proper gloves for task',
  extraPpe: 'Extra PPE available onsite',
  reflectiveVests: 'Reflective vests for spotters',
  // Pre-Job Planning
  dailySafetyMeetings: 'Daily safety meetings Conducted',
  djsaCompleted: 'DJSA/CJSA completed and reviewed',
  hazardsDocumented: 'Task-specific hazards/controls documented',
  permitsInPlace: 'Permits (Client/NTACT) in place',
  validTrainingCards: 'Operations have valid training cards',
  monthlySafetyTraining: 'Monthly safety training held',
  siteOrientation: 'Site orientation completed',
  // Site Safety
  emergencyContacts: 'Emergency contacts posted',
  firstAidSignage: 'First aid signage visible',
  laborLawPosters: 'Labour law Posters (5-in-1, OSHA log, etc.)',
  oneCallTicket: 'One Call ticket for excavation',
  safetyAlerts: 'Safety alerts posted',
  // Work Environment
  smokingAreas: 'Smoking areas marked, with extinguisher',
  equipmentCertified: 'Equipment operated by certified personnel',
  equipmentInspections: 'Equipment inspections documented',
  equipmentParked: 'Equipment safely parked & attachments secured',
  h2sMonitors: 'H2S monitors calibrated and worn properly',
  firstAidKits: 'First aid kits stocked, accessible',
  waterSanitation: 'Water & sanitation facilities provided',
  housekeeping: 'Housekeeping maintained',
  // Working at Heights
  fallProtection: "Fall protection used over 6'",
  laddersScaffolds: 'Ladders/scaffolds inspected, secured, labeled',
  toolsSecured: 'Tools/materials secured in elevated work (Drops Program)',
  overheadProtection: 'Overhead protection to workers when overhead hazards exist',
  openHoles: 'Open holes marked and covered',
  // Tools Electrical
  gfcis: 'GFCIs used and tested',
  cordsProtected: 'Cords protected and grounded',
  toolsInspected: 'Tools inspected, color coded',
  fireExtinguishers: 'Fire extinguishers available & inspected',
  flammableMaterials: 'Flammable materials handled per safety protocols',
  // Welding Gas
  weldingRig: 'Welding rig inspected',
  fireWatch: 'Fire watch available',
  ppeGrinding: 'PPE for grinding (double eye protection)',
  gasCylinders: 'Compressed gas cylinders secured and labeled',
  hosesCondition: 'Hoses in good condition and crimped',
  // Excavation
  oneCallCompleted: 'One Call completed',
  barricaded: 'Excavations barricaded & dry',
  accessLadders: "Access ladders/ramps every 25'",
  properShoring: "Proper shoring/sloping for 5'+",
  utilitiesMarked: 'Utilities marked',
  // Subcontractor
  jsasUsed: "JSA's used for tasks",
  ppeUsed: 'PPE used correctly',
  trainingCards: 'Training cards on hand',
  simopsJsa: 'SIMOPS JSA signed when applicable',
  equipmentFirstAid: 'Equipment and First Aid checked',
  // Emergency Plan
  ssspPlan: 'Is the SSSP plan available and reviewed',
  employeesKnowPlan: 'Do employees know what is in the plan',
  employeesKnowReact: 'Do employees know how to react to an emergency',
  planOnJsa: 'Is the emergency plan noted on JSA',
  accountingSystem: 'Is there a system for accounting everyone onsite',
};

const formatDate = (val, fmt = 'MM-DD-YYYY') => {
  if (!val) return '';
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return String(val);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yyyy = d.getFullYear();
  if (fmt === 'YYYY-MM-DD') return `${yyyy}-${mm}-${dd}`;
  return `${mm}-${dd}-${yyyy}`;
};

export const buildShpJsonData = (formData = {}) => {
  const d = formData;
  const auditors = Array.isArray(d.auditors) ? d.auditors : [];
  const auditorNames = Array.isArray(d.auditorNames) ? d.auditorNames : [];

  const auditorsAnswer = auditors.map((a) => ({
    auditorName: a.auditorName || '',
    designation: a.designation || '',
    uploadedFile: normImg(
      a.uploadedFile ||
      (a.signature && typeof a.signature === 'object' ? a.signature : {}) ||
      {}
    ),
  }));

  const companyList = Array.isArray(d.companyList) ? d.companyList
    : Array.isArray(d.companyJobEntries) ? d.companyJobEntries
    : [];
  const toInt = (v) => (v != null && v !== '' ? parseInt(v, 10) || null : null);
  const companyListAnswer = companyList.length > 0
    ? companyList.map((block) => ({
        company: { id: toInt(block.companyId || block.company?.id), name: block.companyName || block.company?.name || '' },
        job: { job_num: String(block.jobNumber || block.job?.job_num || ''), job_name: block.jobName || block.job?.job_name || '' },
        projectManager: {
          pm_id: toInt(block.pmId || block.projectManager?.pm_id),
          pm_name: block.managerName || block.projectManager?.pm_name || '',
        },
        employee: { emp_id: block.emp_id || block.employee?.emp_id || '', emp_name: block.emp_name || block.employee?.emp_name || '' },
      }))
    : (d.companyId || d.companyid)
      ? [{
          company: { id: toInt(d.companyId || d.companyid), name: d.companyName || d.company_name || '' },
          job: { job_num: String(d.job_id || d.jobNumber || ''), job_name: d.jobName || d.job_description || '' },
          projectManager: { pm_id: toInt(d.pmId || d.pm_id), pm_name: d.managerName || d.pm_name || '' },
          employee: { emp_id: d.emp_id || '', emp_name: d.emp_name || '' },
        }]
      : [];

  const json = [
    {
      question: 'Auditor Name',
      answer: auditorsAnswer?.[0]?.auditorName || auditorNames?.[0] || '',
    },
    { question: 'Audit Date', answer: formatDate(d.auditDate) },
    { question: 'comment', answer: d.auditComment || '' },
    { question: 'commentImages', answer: normImgArray(d.commentImages || []) },
    { question: 'Project Manager Name', answer: d.managerName || '' },
    { question: 'Auditors', answer: auditorsAnswer },
    { question: 'companylist', answer: companyListAnswer },
    { question: 'Project Number', answer: d.projectNumber || '' },
    { question: 'Location', answer: d.location || '' },
  ];

  SHP_SECTIONS.forEach(([sectionName, stateKey]) => {
    const obj = d[stateKey] && typeof d[stateKey] === 'object' ? d[stateKey] : {};
    const comments = d.checklistComments || {};
    const images = d.checklistImages || {};
    const sectionKeyShort = stateKey.replace('Checks', '');
    Object.entries(obj).forEach(([key, val]) => {
      const label = SHP_LABEL_MAP[key] || key.replace(/([A-Z])/g, ' $1');
      const question = `${sectionName} - ${label}`;
      const itemKey = `${sectionKeyShort}_${key}`;
      const comment = comments[itemKey];
      const itemImages = images[itemKey];
      if (val === 'NO' && comment) {
        json.push({
          question,
          answer: val,
          comment,
          image: normImgArray(Array.isArray(itemImages) ? itemImages : []),
        });
      } else {
        json.push({ question, answer: val });
      }
    });
  });

  if (Array.isArray(d.infractions) && d.infractions.length > 0) {
    json.push({ question: 'Infractions', answer: d.infractions });
  }

  // Supervisor signature — mobile may store as supervisorSignature or signature
  const supSig = d.supervisorSignature || d.signature || null;
  if (supSig) {
    json.push({ question: 'Signatures', answer: [normImg(supSig)] });
  }

  json.push({ question: 'Signature Date', answer: formatDate(new Date(), 'YYYY-MM-DD') });

  return json;
};

// ============================ AIF (Accident Incident) ========================

const checkboxFromMap = (map, options) => {
  const obj = map && typeof map === 'object' ? map : {};
  const opts = options || Object.keys(obj);
  return {
    type: 'checkbox',
    options: opts,
    selectedValues: opts.filter((k) => obj[k]),
  };
};

// Normalize the AIF attachmentsFiles map: { "0": [{file_name,...}], "1": [...] }
const normalizeAttachmentFiles = (filesMap) => {
  if (!filesMap || typeof filesMap !== 'object') return filesMap;
  const result = {};
  Object.entries(filesMap).forEach(([key, arr]) => {
    result[key] = Array.isArray(arr) ? normImgArray(arr) : arr;
  });
  return result;
};

export const buildAifJsonData = (formData = {}) => {
  const d = formData;

  const data = {
    companyId: d.companyId != null ? parseInt(d.companyId, 10) : null,
    emp_id: d.emp_id,
    method: 'Filled Out',
    jobNumber: d.job_id,
    empname: d.emp_name,
    date: formatDate(d.savedAt || new Date(), 'YYYY-MM-DD'),
    imgfiles: [],
    employeename: d.emp_name || '',
    employeebadge: d.emp_id || '',
    companyname: d.companyName || '',
    job_description: d.job_description || '',
    phonenumber: d.phoneNumber || '',
    address: d.address || '',
    city: d.city || '',
    state: d.state || '',
    zipcode: d.zipCode || '',
    incidentdate: d.timeOfIncident || '',
    department: {
      type: 'radio',
      values: ['I&E', 'Constructors'],
      selectedValue: d.departmentName || 'I&E',
    },
    jobtitle: d.jobTitle || '',
    projectnumber: d.projectNumber || '',
    projectlocation: d.projectLocation || '',
    directsupervisor: d.directSupervisor || '',
    client: d.clientName || '',
    wheathercondition: d.weatherCondition || '',
    clientbeennotified: {
      type: 'radio',
      values: ['Yes', 'No'],
      selectedValue: d.clientNotified || '',
    },
    damages: checkboxFromMap(d.incidentClassifications),
    injuredside: {
      type: 'radio',
      options: ['Left', 'Right', 'Bilateral', 'N/A'],
      selectedValue: d.injuredSide || '',
    },
    injuryDescription: d.injuryDescription || '',
    bodypartsinjuried: checkboxFromMap(d.bodyPartsInjured),
    injurytype: {
      type: 'dropdown',
      options: [],
      selectedvalue: d.injuryType || '',
    },
    enterManually: d.enterManually,
    incidentDescription: d.accidentDescription || '',
    incidentcause: {
      type: 'dropdown',
      options: [],
      selectedvalue: d.incidentCause || '',
    },
    descriptionofincident: d.accidentDescription || '',
    witnessstatement: d.witnessInformation || '',
    anyWitness: d.witnessInformation || '',
    witnessCards: d.witnessList || [],
    witnessSummary: d.witnessInformation || '',
    postaccidentua: {
      type: 'checkbox',
      options: ['Post-accident drug/alcohol test required'],
      selectedValues: d.postAccidentTest
        ? ['Post-accident drug/alcohol test required']
        : [],
    },
    testingInformationList: (d.testingInformationList || []).map((entry) => ({
      facility: entry.facility || '',
      collector: entry.collector || '',
      testtype: {
        type: 'Checkbox',
        options: ['Non-Dot', 'Dot'],
        selectedValue: entry.testType || 'Non-Dot',
      },
    })),
    testtype: {
      type: 'Checkbox',
      options: ['Non-Dot', 'Dot'],
      selectedValue: (d.testingInformationList || [])[0]?.testType || 'Non-Dot',
    },
    facility: (d.testingInformationList || [])[0]?.facility || '',
    collector: (d.testingInformationList || [])[0]?.collector || '',
    employeeInformation: (d.employeeInformationList || []).map((entry) => ({
      employee_name: entry.employeeName || '',
      employee_phone_number: entry.employeePhoneNumber || '',
    })),
    employeeInformationList: (d.employeeInformationList || []).map((entry) => ({
      employee_name: entry.employeeName || '',
      employee_phone_number: entry.employeePhoneNumber || '',
    })),
    attachments: {
      type: 'checkbox',
      options: [
        'Pictures',
        'Additional Drawing/Diagrams',
        'JSA',
        'Incident Pictures (Re-enactment if possible)',
      ],
      selectedvalues: (d.attachmentsTypes && d.attachmentsTypes.length > 0)
        ? d.attachmentsTypes
        : [],
      Files: normalizeAttachmentFiles(d.attachmentsFiles || {}),
    },
    incidentsummary: d.incidentSummary || '',
    employeenameprint: d.emp_name || '',
    employeenameprintsignature: d.emp_name || '',
    employeenameprintsignaturedate: formatDate(d.savedAt || new Date(), 'YYYY-MM-DD'),
    supervisornameprint: d.directSupervisor || '',
    supervisorprintsignature: d.directSupervisor || '',
    supervisorprintsignaturedate: formatDate(d.savedAt || new Date(), 'YYYY-MM-DD'),
    employeeStatements: (d.employeeStatements || []).map((e) => ({
      id: e.id || Math.random().toString(36).substr(2, 9),
      jobTitle: e.jobTitle,
      timeInCraft: e.timeInCraft,
      timeOnJobsite: e.timeOnJobsite,
      description: e.description,
      name: e.name,
      signature: isUploadedRef(e.signature)
        ? normImg(e.signature)
        : (e.signature && typeof e.signature === 'string' && !e.signature.startsWith('data:')
          ? e.signature
          : e.signature || ''),
      date: formatDate(e.date, 'YYYY-MM-DD'),
    })),
    witnessStatements: (d.witnessStatements || []).map((w) => ({
      id: w.id || Math.random().toString(36).substr(2, 9),
      jobTitle: w.jobTitle,
      timeInCraft: w.timeInCraft,
      timeOnJobsite: w.timeOnJobsite,
      description: w.description,
      name: w.name,
      signature: isUploadedRef(w.signature)
        ? normImg(w.signature)
        : (w.signature && typeof w.signature === 'string' && !w.signature.startsWith('data:')
          ? w.signature
          : w.signature || ''),
      date: formatDate(w.date, 'YYYY-MM-DD'),
    })),
    supervisorStatements: (d.supervisorStatements || []).map((s) => ({
      id: s.id || Math.random().toString(36).substr(2, 9),
      jobTitle: s.jobTitle,
      timeInCraft: s.timeInCraft,
      timeOnJobsite: s.timeOnJobsite,
      description: s.description,
      name: s.name,
      signature: isUploadedRef(s.signature)
        ? normImg(s.signature)
        : (s.signature && typeof s.signature === 'string' && !s.signature.startsWith('data:')
          ? s.signature
          : s.signature || ''),
      date: formatDate(s.date, 'YYYY-MM-DD'),
    })),
    photoStatements: (d.photoInformation || []).map((p) => ({
      id: p.id || Math.random().toString(36).substr(2, 9),
      photoNo: p.photoNo,
      photoDate: formatDate(p.photoDate, 'YYYY-MM-DD'),
      timeOfDay: p.timeOfDay,
      location: p.location,
      description: p.description,
      notes: p.notes,
      photographer: p.photographer,
    })),
    isSkipVechileAccident: d.skippedVehicleStep,
    causalFactorsForm: {
      proceduresProgramsManagement: checkboxFromMap(d.proceduresManagement),
      humanFactors: checkboxFromMap(d.humanFactors),
      workDirectionCommunication: checkboxFromMap(d.workDirectionCommunication),
      workEnvironment: checkboxFromMap(d.workEnvironment),
      transportation: checkboxFromMap(d.transportationFactors),
      lessonsLearned: { key_lessons: d.keyLessons || '' },
      correctiveActions: {
        immediate_action_details: d.immediateActionDetails || '',
        target_completion_date: formatDate(d.targetCompletionDate, 'YYYY-MM-DD'),
      },
    },
    motorVehicleReport: {
      driverName: d.driverName || '',
      isDriverApproved: d.approvedDriverList || '',
      diagramimg: '',
      licenseNumber: d.driverLicenseNumber || '',
      issuingState: d.issuingState || '',
      licenseExpiration: d.licenseExpiration || '',
      vehicleType: {
        ntactVehicle: d.vehicleType === 'NTACT Vehicle',
        ntactLeased: d.vehicleType === 'NTACT Leased Vehicle',
        mobileEquipment: d.vehicleType === 'Mobile Equipment',
      },
      vehicleDetails: d.vehicleDetails || {},
      incidentDetails: {
        weatherConditions: d.weatherCondition || '',
        speed: {
          employee: d.employeeSpeed || '',
          other: d.otherVehicleSpeed || '',
          posted: d.postedSpeedLimit || '',
        },
        nonEmployeeInjuries: d.nonEmployeeInjury || '',
        propertyDamage: d.nonEmployeePropertyDamage || '',
        incidentDescription: d.incidentDescription || '',
      },
    },
  };

  return [data];
};

// ============================ Dispatcher ====================================

// Maps the human-readable draft form_name → short form-type code.
export const FORM_NAME_TO_TYPE = {
  'Accident Incident Investigation Form': 'AIF',
  'Behavior Based Safety Observation Form': 'BBS',
  'Weekly Driver Log Safety Form': 'WDS',
  'Safety Health Primary Audit Form': 'SHP',
  'Trailer Inspection Form': 'TRAILER',
};

// Build a SafetyForm-row-shaped object (the same shape returned by
// /v1/forms/get_by_id) from a mobile draft. Consumed by the dashboard view
// modals and by each edit form's mapJsonToForm() loader.
export const draftToFormRow = (draft = {}) => {
  const formData = draft.form_data || {};
  const ft = FORM_NAME_TO_TYPE[draft.form_name] || draft.form_name;

  // Mobile SHP/SHPA drafts store company/employee/job/PM inside companyList[0]
  // instead of as top-level fields. Extract the first block so draftToFormRow
  // can populate the row-level fields that mapApiToFormData relies on.
  const fb = (Array.isArray(formData.companyList) && formData.companyList[0]) || {};

  const firstJob =
    (formData.logEntries || [])[0]?.jobNumber
    || formData.job_id
    || fb.jobNumber
    || '';
  const firstJobName =
    (formData.validatedJobs || []).find((j) => j.job_num === String(firstJob))?.job_name
    || formData.job_description
    || formData.jobName
    || fb.jobName
    || '';

  return {
    id: draft.draft_id,
    draft_id: draft.draft_id,
    emp_id: formData.emp_id ?? fb.emp_id ?? null,
    emp_name: formData.emp_name || fb.emp_name || formData.driver_name || draft.created_by || '',
    job_id: firstJob,
    job_description: firstJobName,
    jobDescription: firstJobName,
    observer_name:
      formData.observer_name || formData.observerName || formData.emp_name || fb.emp_name || draft.created_by || '',
    filled_date: formData.filled_date || formData.date || formData.auditDate || '',
    form_type: ft,
    companyid: formData.companyid || formData.companyId || fb.companyId || draft.company_id || null,
    json_data: buildDraftJsonData(ft, formData),
    status: draft.status === 'submitted' ? 'A' : 'D',
    created_by: draft.created_by || '',
    created_at: draft.created_at || '',
    app_version: formData.app_version || '',
    // Pass through fields that edit forms rely on but aren't in json_data.
    // Mobile SHP drafts don't store `method` — default to 'Filled Out'.
    method: formData.method || (ft === 'SHP' || ft === 'SHPA' ? 'Filled Out' : ''),
    pm_id: formData.pm_id || formData.pmId || fb.pmId || null,
    pm_name: formData.pm_name || formData.pmName || formData.managerName || fb.managerName || '',
    company_name: formData.company_name || formData.companyName || fb.companyName || '',
    _isDraft: true,
  };
};

export const buildDraftJsonData = (formType, formData = {}) => {
  // If the draft already carries a web-formatted json_data array, prefer it.
  if (Array.isArray(formData.json_data) && formData.json_data.length > 0) {
    return formData.json_data;
  }

  switch (formType) {
    case 'AIF':
      return buildAifJsonData(formData);
    case 'BBS':
      return buildBbsJsonData(formData);
    case 'SHP':
    case 'SHPA':
      return buildShpJsonData(formData);
    case 'TRAILER':
      return buildTrailerJsonData(formData);
    case 'WDS':
    case 'WDLS':
    default:
      return buildWdsJsonData(formData);
  }
};
